import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Select, Button, DatePicker as AntDatePicker } from '@app/ui';
import { capitalize } from '@app/utils/capitalize';
import { dateOptions } from './options';
import connect from '@app/utils/connect';
import dayjs, { Dayjs } from 'dayjs';

const { Option } = Select;

// Types
interface DatePickerProps {
    param: string;
    setSelectedParam: ({
        param,
        sub,
    }: {
        param: string;
        sub?: string;
    }) => (value: any) => void;
    allSelectedParams: Record<string, any>;
    disabled?: boolean;
    client: {
        valuationStart?: string;
    };
}

// Constants
const DATE_OPTIONS = {
    AFTER: 1,
    BEFORE: 2,
    BETWEEN: 3,
} as const;

const DatePicker: React.FC<DatePickerProps> = ({
    param,
    setSelectedParam,
    allSelectedParams,
    disabled = false,
    client,
}) => {
    const { t } = useTranslation(['tools', 'common']);
    // Get selected dates from params
    const selected = useMemo(
        () => allSelectedParams[param] || {},
        [allSelectedParams, param],
    );

    // State management - only track what we need locally
    const [dateOption, setDateOption] = useState<number | null>(null);
    const [localStartDate, setLocalStartDate] = useState<Dayjs | null>(null);
    const [localEndDate, setLocalEndDate] = useState<Dayjs | null>(null);

    // Get current date option configuration
    const currentOption = useMemo(() => {
        return dateOptions.find(option => option.value === dateOption);
    }, [dateOption]);

    // Determine which date inputs to show
    const showStart = currentOption?.subs.includes('start') || false;
    const showEnd = currentOption?.subs.includes('end') || false;

    // Calculate minimum date for end date picker
    const minDate = useMemo(() => {
        const valuationStart = client.valuationStart
            ? dayjs(client.valuationStart)
            : null;

        const startDate = selected.start ? dayjs(selected.start) : null;

        if (valuationStart && startDate) {
            return valuationStart.isAfter(startDate)
                ? valuationStart
                : startDate;
        }
        return valuationStart || startDate;
    }, [client.valuationStart, selected.start]);

    // Initialize component when param changes
    useEffect(() => {
        if (disabled) return;

        // Reset state when param changes
        setDateOption(null);
        setLocalStartDate(null);
        setLocalEndDate(null);

        // If we have existing selected data, restore it
        if (selected && Object.keys(selected).length > 0) {
            const keys = Object.keys(selected);

            // Determine which option to set based on selected keys
            let optionValue: number;
            if (keys.length > 1) {
                optionValue = DATE_OPTIONS.BETWEEN;
            } else if (keys[0] === 'end') {
                optionValue = DATE_OPTIONS.BEFORE;
            } else {
                optionValue = DATE_OPTIONS.AFTER;
            }

            setDateOption(optionValue);

            // Restore local date values
            if (selected.start) {
                setLocalStartDate(dayjs(selected.start));
            }
            if (selected.end) {
                setLocalEndDate(dayjs(selected.end));
            }
        }
    }, [param, disabled]);

    // Handle date option change
    const handleDateOptionChange = (value: number) => {
        setDateOption(value);
        // Clear selected params and local state when option changes
        setSelectedParam({ param })({});
        setLocalStartDate(null);
        setLocalEndDate(null);
    };

    // Handle date changes - update local state first, then parent
    const handleStartDateChange = (value: Dayjs | null) => {
        setLocalStartDate(value);

        if (!value) {
            // Remove start date
            const newSelected = { ...selected };
            delete newSelected.start;
            setSelectedParam({ param })(newSelected);
        } else {
            // Add/update start date
            const newSelected = {
                ...selected,
                start: value.toDate(),
            };
            setSelectedParam({ param })(newSelected);
        }
    };

    const handleEndDateChange = (value: Dayjs | null) => {
        setLocalEndDate(value);

        if (!value) {
            // Remove end date
            const newSelected = { ...selected };
            delete newSelected.end;
            setSelectedParam({ param })(newSelected);
        } else {
            // Add/update end date
            const newSelected = {
                ...selected,
                end: value.toDate(),
            };
            setSelectedParam({ param })(newSelected);
        }
    };

    return (
        <Row gutter={16} align="middle">
            <Col span={8}>
                {!disabled ? (
                    <Select
                        style={{ width: '100%' }}
                        placeholder={t(
                            'tools:automatedParams.placeholders.selectDateFilter',
                        )}
                        value={dateOption}
                        onChange={handleDateOptionChange}
                        disabled={disabled}
                    >
                        {dateOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {t(
                                    `tools:automatedParams.options.comparison.${
                                        option.value === DATE_OPTIONS.AFTER
                                            ? 'onOrAfter'
                                            : option.value ===
                                                DATE_OPTIONS.BEFORE
                                              ? 'onOrBefore'
                                              : 'inBetween'
                                    }`,
                                )}
                            </Option>
                        ))}
                    </Select>
                ) : (
                    currentOption && (
                        <Button
                            type="primary"
                            style={{ width: '100%' }}
                            disabled={disabled}
                        >
                            {capitalize(
                                t(
                                    `tools:automatedParams.options.comparison.${
                                        currentOption.value ===
                                        DATE_OPTIONS.AFTER
                                            ? 'onOrAfter'
                                            : currentOption.value ===
                                                DATE_OPTIONS.BEFORE
                                              ? 'onOrBefore'
                                              : 'inBetween'
                                    }`,
                                ),
                            )}
                        </Button>
                    )
                )}
            </Col>

            <Col span={6}>
                {showStart && (
                    <AntDatePicker
                        value={localStartDate}
                        onChange={handleStartDateChange}
                        style={{ width: '100%' }}
                        placeholder={t(
                            'tools:automatedParams.placeholders.startDate',
                        )}
                        disabled={disabled}
                    />
                )}
                {!showStart && showEnd && (
                    <AntDatePicker
                        value={localEndDate}
                        onChange={handleEndDateChange}
                        style={{ width: '100%' }}
                        placeholder={t(
                            'tools:automatedParams.placeholders.endDate',
                        )}
                        disabled={disabled}
                    />
                )}
            </Col>

            {showStart && showEnd && (
                <>
                    <Col
                        span={2}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span>
                            {t('tools:automatedParams.options.comparison.and')}
                        </span>
                    </Col>
                    <Col span={6}>
                        <AntDatePicker
                            value={localEndDate}
                            onChange={handleEndDateChange}
                            style={{ width: '100%' }}
                            placeholder={t(
                                'tools:automatedParams.placeholders.endDate',
                            )}
                            disabled={disabled}
                            minDate={minDate}
                        />
                    </Col>
                </>
            )}
        </Row>
    );
};

export default connect(['client'])(DatePicker);
